import { CronExpressionParser } from "cron-parser";
import moment from "moment-timezone";
import React, { useEffect, useMemo } from "react";
import {
    Alert,
    Badge,
    Button,
    ButtonGroup,
    Card,
    Col,
    Dropdown,
    ListGroup,
    Modal,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap";
import { BsAlarmFill } from "react-icons/bs";
import {
    FaBatteryFull,
    FaCamera,
    FaChevronRight,
    FaClock,
    FaCog,
    FaCube,
    FaExclamationTriangle,
    FaFolder,
    FaGlasses,
    FaHistory,
    FaHome,
    FaImage,
    FaMap,
    FaPause,
    FaPlay,
    FaPowerOff,
    FaQrcode,
    FaRedo,
    FaRoute,
    FaServer,
    FaSkull,
} from "react-icons/fa";
import { FaFileWaveform } from "react-icons/fa6";
import { MdBlock } from "react-icons/md";
import { TbZzz } from "react-icons/tb";
import { Link, useNavigate, useParams } from "react-router";

import * as api from "../../api/api";
import {
    useDockCollection,
    useDroneConfigDocument,
    useLocationDocument,
    useMissionDocument,
    useMissionHandlerClusterDocument,
    useRouteplannerDocument,
    useRoutineCollection,
    useVersionsDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { useDroneStateRtdbDocument } from "../../api/rtdbWrappers.generated.jsx";
import { droneConfigChangelogRoute } from "../../changelogRoutes.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import DevMenuButton from "../../components/DevMenuButton";
import DroneAccuracyStat from "../../components/DroneAccuracyStat";
import DroneBatteryStat from "../../components/DroneBatteryStat";
import DroneDiskSpaceStat from "../../components/DroneDiskSpaceStat";
import DroneStateStat from "../../components/DroneStateStat";
import DroneWifiStat from "../../components/DroneWifiStat";
import EmergencyLandSlider from "../../components/EmergencyLandSlider";
import FirestoreDevMenuItem from "../../components/FirestoreDevMenuItem";
import Layout, { Title } from "../../components/Layout/Layout";
import LogDevMenuItem from "../../components/LogDevMenuItem";
import MissionList from "../../components/MissionList";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import UserQuestion from "../../components/UserQuestion";
import { useUser } from "../../contexts/user_provider";
import { useDroneOnline } from "../../hooks/useDroneOnline.jsx";
import useIsSmallScreen from "../../hooks/useIsSmallScreen.jsx";
import urls from "../../urls.jsx";
import { MissionHandlerAlerts } from "../MissionPage/index.jsx";

function ErrorMessage({ errors, droneId, className }) {
    const isSmallScreen = useIsSmallScreen();

    function onCloseErrors() {
        // We need to clear errors via the API because we don't have write rights for droneStates document
        api.clearDroneErrors(droneId);
    }

    const errorList = errors
        ? Object.values(errors)
              .filter((error) => error?.message)
              .sort(
                  (a, b) =>
                      (b.serverTs ?? b.droneTs ?? 0) -
                      (a.serverTs ?? a.droneTs ?? 0),
              )
        : [];

    return (
        <Alert
            className={className}
            variant="danger"
            onClose={onCloseErrors}
            dismissible
        >
            <ul className="m-0 d-flex flex-column gap-2">
                {errorList.map((error, index) => {
                    const ts = error.serverTs ?? error.droneTs;

                    return (
                        <li
                            key={`${error.message}-${ts ?? index}`}
                            className="d-flex justify-content-between gap-3"
                        >
                            <span>{error.message}</span>

                            {ts && (
                                <Badge bg="secondary" className="ms-2">
                                    <TimeDisplay
                                        timestamp={Math.floor(
                                            new Date(ts).getTime() / 1000,
                                        )}
                                        format="YYYY-MM-DD#HH:mm:ss"
                                        hideDateIfToday={true}
                                        breakOnHashtag={isSmallScreen}
                                        location={location}
                                    />
                                </Badge>
                            )}
                        </li>
                    );
                })}
            </ul>
        </Alert>
    );
}

export function DroneMissionList(props) {
    const { organizationId, locationId, droneId } = useParams();
    const [missions] = api.useDroneMissions(
        organizationId,
        locationId,
        droneId,
        props.limit,
    );
    const [routines] = useRoutineCollection(organizationId, locationId);
    const [location] = useLocationDocument(organizationId, locationId, {
        mode: "once",
    });
    const timezone = location?.timezone || "UTC";

    return (
        <MissionList
            className={props.className}
            routines={routines}
            organizationId={organizationId}
            locationId={locationId}
            missions={missions}
            droneId={droneId}
        >
            {routines
                .filter(
                    (routine) => routine.enabled && routine.droneId === droneId,
                )
                .sort((a, b) => {
                    try {
                        const aNext = CronExpressionParser.parse(a.interval, {
                            tz: timezone,
                        })
                            .next()
                            .toDate();
                        const bNext = CronExpressionParser.parse(b.interval, {
                            tz: timezone,
                        })
                            .next()
                            .toDate();
                        return aNext - bNext;
                    } catch {
                        return 0;
                    }
                })
                .map((routine) => {
                    let routineTimeDisplay;
                    try {
                        routineTimeDisplay = (
                            <TimeDisplay
                                timestamp={moment(
                                    CronExpressionParser.parse(
                                        routine.interval,
                                        { tz: timezone },
                                    )
                                        .next()
                                        .toDate(),
                                ).unix()}
                                format="ddd HH:mm"
                                organizationId={organizationId}
                                locationId={locationId}
                            />
                        );
                    } catch {
                        routineTimeDisplay = "Invalid";
                    }

                    return (
                        <ListGroup.Item
                            className="px-4 py-2 d-flex justify-content-between align-items-start"
                            key={routine.id}
                            as={Link}
                            to={urls.routine(
                                organizationId,
                                locationId,
                                routine.id,
                            )}
                            action
                        >
                            <div>
                                <FaClock
                                    className="me-4 text-primary"
                                    style={{ marginTop: "-2px" }}
                                />
                                {routine.name}
                            </div>
                            <div>
                                <Badge pill bg="primary" className="me-1">
                                    <FaClock
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    {routineTimeDisplay}
                                </Badge>
                                <FaChevronRight style={{ color: "#b9b9b9" }} />
                            </div>
                        </ListGroup.Item>
                    );
                })}
        </MissionList>
    );
}

function DroneSettingsButton(props) {
    return (
        <Button
            variant={props.variant}
            as={Link}
            to={urls.locationDroneSettings(
                props.drone.organizationId,
                props.drone.locationId,
                props.drone.id,
            )}
        >
            <FaCog
                className="me-2"
                style={{
                    marginTop: "-2px",
                }}
            />
            Settings
        </Button>
    );
}

function DroneStartButton(props) {
    return (
        <Button
            variant="primary"
            as={Link}
            to={urls.droneStart(
                props.drone.organizationId,
                props.drone.locationId,
                props.drone.id,
            )}
        >
            <FaPlay
                className="me-2"
                style={{
                    marginTop: "-2px",
                }}
            />
            Start
        </Button>
    );
}

function AdminDroneButton(props) {
    const { getUserIsAdmin } = useUser();
    const isDroneOnline = useDroneOnline(props.drone.id);

    return (
        <>
            {getUserIsAdmin() && (
                <DevMenuButton>
                    <Dropdown.Item
                        as={Link}
                        to={urls.locationDroneNerd(
                            props.drone.organizationId,
                            props.drone.locationId,
                            props.drone.id,
                        )}
                    >
                        <FaGlasses
                            className="me-2"
                            style={{
                                marginTop: "-2px",
                            }}
                        />
                        View State
                    </Dropdown.Item>
                    <Dropdown.Item
                        as={Link}
                        to={urls.locationDroneLivestream(
                            props.drone.organizationId,
                            props.drone.locationId,
                            props.drone.id,
                        )}
                    >
                        <FaCamera
                            className="me-2"
                            style={{
                                marginTop: "-2px",
                            }}
                        />
                        Livestream
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <FirestoreDevMenuItem
                        text="Config"
                        as={Dropdown.Item}
                        path={`droneConfigs/${props.drone.id}`}
                    />
                    <FirestoreDevMenuItem
                        text="State"
                        as={Dropdown.Item}
                        path={`droneStates/${props.drone.id}`}
                        rtdb={true}
                    />
                    <FirestoreDevMenuItem
                        text="Commands"
                        as={Dropdown.Item}
                        path={`droneCommands/${props.drone.id}`}
                    />
                    <LogDevMenuItem
                        as={Dropdown.Item}
                        name="drone"
                        value={props.drone.id}
                    />
                </DevMenuButton>
            )}
            <ButtonGroup>
                {/* draw the start button or the settings button */}
                {isDroneOnline &&
                !api.droneHasActiveMissions(props.droneState) ? (
                    <DroneStartButton drone={props.drone} />
                ) : (
                    <DroneSettingsButton
                        drone={props.drone}
                        variant={isDroneOnline ? "primary" : "secondary"}
                    />
                )}
                {/* draw the drop down button button, gray or blue, full options or  */}
                {isDroneOnline ? (
                    <Dropdown as={ButtonGroup} align="end">
                        <Dropdown.Toggle
                            variant="primary"
                            id="dropdown-online"
                        />
                        <Dropdown.Menu>
                            <Dropdown.Item
                                as={Link}
                                to={urls.locationDroneSettings(
                                    props.drone.organizationId,
                                    props.drone.locationId,
                                    props.drone.id,
                                )}
                            >
                                <FaCog
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Settings
                            </Dropdown.Item>
                            <Dropdown.Item
                                as={Link}
                                to={droneConfigChangelogRoute({
                                    droneId: props.drone.id,
                                })}
                            >
                                <FaHistory
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Changelogs
                            </Dropdown.Item>
                            <Dropdown.Item
                                as={Link}
                                to={urls.payloadSettings(props.drone.payloadId)}
                            >
                                <FaCamera
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Payload settings
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => {
                                    props.setShowShutdownModal(true);
                                }}
                            >
                                <FaPowerOff
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Shutdown
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => {
                                    props.setShowRebootModal(true);
                                }}
                            >
                                <FaRedo
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Reboot
                            </Dropdown.Item>
                            {props.droneState?.widgets?.status?.data?.toLowerCase() !==
                                "sleeping" && (
                                <Dropdown.Item
                                    onClick={() => {
                                        api.sendGoToSleep(props.drone.id);
                                    }}
                                >
                                    <TbZzz
                                        className="me-2"
                                        style={{
                                            marginTop: "-2px",
                                        }}
                                    />
                                    Go to sleep
                                </Dropdown.Item>
                            )}
                            {props.droneState?.widgets?.status?.data?.toLowerCase() ===
                                "sleeping" && (
                                <Dropdown.Item
                                    onClick={() => {
                                        api.sendWakeUp(props.drone.id);
                                    }}
                                >
                                    <BsAlarmFill
                                        className="me-2"
                                        style={{
                                            marginTop: "-2px",
                                        }}
                                    />
                                    Wake up
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                ) : (
                    <Dropdown as={ButtonGroup} align="end">
                        <Dropdown.Toggle
                            variant="secondary"
                            id="dropdown-offline"
                        />
                        <Dropdown.Menu>
                            <Dropdown.Item
                                as={Link}
                                to={droneConfigChangelogRoute({
                                    droneId: props.drone.id,
                                })}
                            >
                                <FaHistory
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Changelogs
                            </Dropdown.Item>
                            <Dropdown.Item
                                as={Link}
                                to={urls.payloadSettings(props.drone.payloadId)}
                            >
                                <FaCamera
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Payload settings
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </ButtonGroup>
        </>
    );
}

function UserDroneButton(props) {
    const isDroneOnline = useDroneOnline(props.drone.id);

    return (
        <ButtonGroup>
            {/* draw the start button or the settings button */}
            {isDroneOnline && !api.droneHasActiveMissions(props.droneState) ? (
                <DroneStartButton drone={props.drone} />
            ) : (
                <DroneSettingsButton drone={props.drone} variant={"primary"} />
            )}
            {isDroneOnline && (
                <Dropdown as={ButtonGroup} align="end">
                    <Dropdown.Toggle
                        variant="primary"
                        id="dropdown-online-user"
                    />
                    <Dropdown.Menu>
                        <Dropdown.Item
                            as={Link}
                            to={urls.locationDroneSettings(
                                props.drone.organizationId,
                                props.drone.locationId,
                                props.drone.id,
                            )}
                        >
                            <FaCog
                                className="me-2"
                                style={{
                                    marginTop: "-2px",
                                }}
                            />
                            Settings
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                props.setShowShutdownModal(true);
                            }}
                        >
                            <FaPowerOff
                                className="me-2"
                                style={{
                                    marginTop: "-2px",
                                }}
                            />
                            Shutdown
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                props.setShowRebootModal(true);
                            }}
                        >
                            <FaRedo
                                className="me-2"
                                style={{
                                    marginTop: "-2px",
                                }}
                            />
                            Reboot
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            )}
        </ButtonGroup>
    );
}

export function DroneStatus({ droneStatus }) {
    return (
        <>
            {!!droneStatus && (
                <Alert variant="primary" className="px-4 m-0">
                    <ul className="m-0 px-4">
                        {droneStatus.data.split(";").map((status, index) => {
                            // biome-ignore lint/suspicious/noArrayIndexKey: Status entries are positional, may contain duplicates, and have no stable identifier.
                            return <li key={`${status}-${index}`}>{status}</li>;
                        })}
                    </ul>
                </Alert>
            )}
        </>
    );
}

function Drone(props) {
    const [showShutdownModal, setShowShutdownModal] = React.useState(false);
    const [showRebootModal, setShowRebootModal] = React.useState(false);
    const [showKillModal, setShowKillModal] = React.useState(false);
    const [showReturnToHomeModal, setShowReturnToHomeModal] =
        React.useState(false);
    const [showEmergencyModal, setShowEmergencyModal] = React.useState(false);
    const [showWifiModal, setShowWifiModal] = React.useState(false);

    const activeMissionId = useMemo(
        () => props.droneState?.missions?.[0]?.missionId,
        [props.droneState?.missions],
    );
    const [activeMission] = useMissionDocument(
        props.drone.organizationId,
        props.drone.locationId,
        activeMissionId,
    );
    const [versions] = useVersionsDocument();

    const [docks] = useDockCollection(
        props.drone.organizationId,
        props.drone.locationId,
    );
    const { getUserIsAdmin } = useUser();
    const navigate = useNavigate();
    const isDroneOnline = useDroneOnline(props.drone.id);
    const isDroneSleeping =
        props.droneState?.widgets?.status?.data?.toLowerCase() === "sleeping";
    const wifiPageIp = `http://${props.droneState?.widgets?.voxl?.data?.ip}`;

    const [logUploaderStatus, setLogUploaderStatus] = React.useState("");
    const [payloadUploaderStatus, setPayloadUploaderStatus] =
        React.useState("");
    const [routeplannerDoc] = useRouteplannerDocument(
        props?.drone?.routeplanner?.version,
    );

    function parseVersion(str) {
        const [, version, hash] =
            str?.match(/([^/]+?)-0-g([0-9a-fA-F]+)/) || [];
        return { version, hash };
    }

    function getVersionVariant({ isOnline, version, policy }) {
        if (!version) return "secondary";
        if (!isOnline) return "secondary";
        if (version.includes("dev")) return "primary";

        if (policy?.upToDate?.includes(version)) return "success";
        if (policy?.updateRecommended?.includes(version)) return "warning";

        return "danger";
    }

    const versionsData = props.droneState?.widgets?.versions?.data;

    const { version: droneControlVersion, hash: droneControlCommitHash } =
        parseVersion(versionsData?.droneControl);
    const { version: yoctoNodesVersion, hash: yoctoNodesCommitHash } =
        parseVersion(versionsData?.yoctoNodes);
    const versionComponents = [
        {
            key: "droneControl",
            label: "DC",
            version: droneControlVersion,
            link: droneControlCommitHash
                ? `https://gitlab.com/CorvusDrones/corvus-drone-control/-/commits/${droneControlCommitHash}`
                : undefined,
        },
        {
            key: "yoctoNodes",
            label: "YN",
            version: yoctoNodesVersion,
            link: yoctoNodesCommitHash
                ? `https://gitlab.com/CorvusDrones/corvus-yocto-nodes/-/commits/${yoctoNodesCommitHash}`
                : undefined,
        },
        ...(props.drone.model === "E13"
            ? [
                  ...(versionsData?.sdk !== "1.6.3"
                      ? [
                            {
                                key: "voxlMavlinkServer",
                                label: "mav",
                                version: versionsData?.voxlMavlinkServer,
                            },
                            {
                                key: "voxlOpenVinsServer",
                                label: "vins",
                                version: versionsData?.voxlOpenVinsServer,
                            },
                            {
                                key: "voxlCameraServer",
                                label: "cam",
                                version: versionsData?.voxlCameraServer,
                            },
                        ]
                      : []),
                  { key: "sdk", label: "sdk", version: versionsData?.sdk },
                  {
                      key: "openVinsConfigMD5",
                      label: "vins-config",
                      version: versionsData?.openVinsConfigMD5,
                      showVersion: false,
                  },
              ]
            : []),
    ];

    // biome-ignore lint/correctness/useExhaustiveDependencies: Reset when the drone inputs change; the helper is a pure read of those inputs and is recreated on every render.
    React.useEffect(() => {
        // Reset state when drone stops flying
        if (!thisDroneIsFlying()) {
            setShowEmergencyModal(false);
            setShowKillModal(false);
        }
    }, [props.drone, props.droneState]);

    function thisDroneIsFlying() {
        if (!props.drone) return false;
        if (!props.droneState) return false;
        if (!props.droneState.widgets) return false;

        return api.droneIsFlying(props.droneState.widgets);
    }

    function anyErrors() {
        if (!props.droneState) return false;
        if (!props.droneState.errors) return false;
        return Object.keys(props.droneState.errors).length > 0;
    }

    function showReturnToHomeButton() {
        return props.droneState?.widgets?.bestReturnCommand?.data !== undefined;
    }

    function showPauseButton() {
        return props.droneState?.widgets?.isPaused?.data !== undefined;
    }

    function showMapButton() {
        return props.droneState?.widgets?.currentFlightId?.data !== undefined;
    }

    React.useEffect(() => {
        function getStatus(mode) {
            if (!mode) return;
            const uploadStatus =
                mode === "measurements"
                    ? props.droneState?.widgets?.payloadUploadStatus?.data
                    : props.droneState?.widgets?.logUploadStatus?.data;

            const regex = new RegExp(
                `(?:Upload is )?(blocked|disabled)?;?\\s*(\\d+)\\s+folders?,\\s+(\\d+)\\s+${mode}\\s+(\\([\\d.]+\\s*[A-Z]+\\))`,
            );

            const match = uploadStatus?.match(regex);
            if (match) {
                let color = "primary";
                if (match[3] === "0") color = "success";
                if (match[1] === "blocked") color = "secondary";
                if (match[1] === "disabled") color = "danger";
                return {
                    state: match[1],
                    files: match[3],
                    folders: match[2],
                    size: match[4],
                    color,
                };
            }
        }

        setLogUploaderStatus(getStatus("files"));
        setPayloadUploaderStatus(getStatus("measurements"));
    }, [
        props.droneState?.widgets?.payloadUploadStatus?.data,
        props.droneState?.widgets?.logUploadStatus?.data,
    ]);

    const showDiskSpaceState =
        props.location?.visualization?.showDiskSpaceOnDronePage &&
        props.droneState?.widgets?.diskAvailableMB?.data !== undefined &&
        props.droneState?.widgets?.diskTotalMB?.data !== undefined;

    return (
        <>
            <Modal
                show={showShutdownModal}
                onHide={() => {
                    setShowShutdownModal(false);
                }}
            >
                <Modal.Header
                    onClick={() => {
                        setShowShutdownModal(false);
                    }}
                >
                    <Modal.Title>Shutdown Drone</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to <b>shutdown</b> drone{" "}
                    <b>{props.drone.id}</b>?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary-outline"
                        onClick={() => {
                            setShowShutdownModal(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            api.sendShutdown(props.drone.id);
                            setShowShutdownModal(false);
                        }}
                    >
                        <FaPowerOff
                            className="me-2"
                            style={{
                                marginTop: "-2px",
                            }}
                        />
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showRebootModal}
                onHide={() => {
                    setShowRebootModal(false);
                }}
            >
                <Modal.Header
                    onClick={() => {
                        setShowRebootModal(false);
                    }}
                >
                    <Modal.Title>Reboot Drone</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to <b>reboot</b> drone{" "}
                    <b>{props.drone.id}</b>?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary-outline"
                        onClick={() => {
                            setShowRebootModal(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            api.sendReboot(props.drone.id);
                            setShowRebootModal(false);
                        }}
                    >
                        <FaRedo
                            className="me-2"
                            style={{
                                marginTop: "-2px",
                            }}
                        />
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showKillModal}
                onHide={() => {
                    setShowKillModal(false);
                }}
            >
                <Modal.Header
                    onClick={() => {
                        setShowKillModal(false);
                    }}
                >
                    <Modal.Title>Kill Drone</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to <b>kill</b> drone{" "}
                    <b>{props.drone.id}</b>?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary-outline"
                        onClick={() => {
                            setShowKillModal(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            api.sendKill(
                                props.drone.id,
                                props.droneState?.missions,
                            );
                            setShowKillModal(false);
                        }}
                    >
                        <FaSkull
                            className="me-2"
                            style={{
                                marginTop: "-2px",
                            }}
                        />
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showReturnToHomeModal}
                onHide={() => {
                    setShowReturnToHomeModal(false);
                }}
            >
                <Modal.Header
                    onClick={() => {
                        setShowReturnToHomeModal(false);
                    }}
                >
                    <Modal.Title>Return to home</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want <b>{props.drone.id}</b> to{" "}
                    <b>return to home</b>? <br />
                    This will also abort the mission.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary-outline"
                        onClick={() => {
                            setShowReturnToHomeModal(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            api.sendAbort(
                                props.drone.id,
                                props.droneState?.missions,
                            );
                            setShowReturnToHomeModal(false);
                        }}
                    >
                        <FaHome
                            className="me-2"
                            style={{
                                marginTop: "-2px",
                            }}
                        />
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showEmergencyModal}
                onHide={() => {
                    setShowEmergencyModal(false);
                }}
            >
                <Modal.Header
                    onClick={() => {
                        setShowEmergencyModal(false);
                    }}
                >
                    <Modal.Title>Emergency land Drone</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to <b>emergency land</b> drone{" "}
                    <b>{props.drone.id}</b>?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary-outline"
                        onClick={() => {
                            setShowEmergencyModal(false);
                        }}
                    >
                        No
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            api.sendEmergencyLand(
                                props.drone.id,
                                props.droneState?.missions,
                            );
                            setShowEmergencyModal(false);
                        }}
                    >
                        <FaSkull
                            className="me-2"
                            style={{
                                marginTop: "-2px",
                            }}
                        />
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showWifiModal}
                onHide={() => {
                    setShowWifiModal(false);
                }}
            >
                <Modal.Header
                    onClick={() => {
                        setShowWifiModal(false);
                    }}
                >
                    <Modal.Title>Wi-Fi page</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        The drone&apos;s Wi-Fi page is available only when
                        it&apos;s powered on and your device is connected to the
                        Wi-Fi network{" "}
                        <b>{props.droneState?.widgets?.voxl?.data?.ssid}</b>.
                        <br />
                        <i style={{ color: "#888", fontSize: "0.9em" }}>
                            If the button doesn&apos;t work, check the drone and
                            your Wi-Fi connection.
                        </i>
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary-outline"
                        onClick={() => {
                            setShowWifiModal(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button href={wifiPageIp}>Open Drone Wi-Fi Page</Button>
                </Modal.Footer>
            </Modal>
            <Layout>
                <title>{`${props.drone.id} | MyCorvus`}</title>
                <Breadcrumbs
                    path={breadcrumbPaths.drone}
                    location={props.location}
                />
                <Card className="mb-4">
                    <Title
                        title={`Drone ${props.drone.id}`}
                        backButtonUrl={urls.locationDrones(
                            props.drone.organizationId,
                            props.drone.locationId,
                        )}
                    >
                        {getUserIsAdmin() && (
                            <AdminDroneButton
                                drone={props.drone}
                                droneState={props.droneState}
                                setShowShutdownModal={setShowShutdownModal}
                                setShowRebootModal={setShowRebootModal}
                                setShowKillModal={setShowKillModal}
                                setShowEmergencyModal={setShowEmergencyModal}
                                setShowReturnToHomeModal={
                                    setShowReturnToHomeModal
                                }
                            />
                        )}
                        {!getUserIsAdmin() && (
                            <UserDroneButton
                                drone={props.drone}
                                droneState={props.droneState}
                                setShowKillModal={setShowKillModal}
                                setShowEmergencyModal={setShowEmergencyModal}
                                setShowShutdownModal={setShowShutdownModal}
                                setShowRebootModal={setShowRebootModal}
                                setShowReturnToHomeModal={
                                    setShowReturnToHomeModal
                                }
                            />
                        )}
                    </Title>
                    {anyErrors() && (
                        <Card.Body className="p-4 pt-0">
                            <ErrorMessage
                                errors={props.droneState.errors}
                                organizationId={props.drone.organizationId}
                                locationId={props.drone.locationId}
                                droneId={props.drone.id}
                                className="m-0"
                            />
                        </Card.Body>
                    )}
                    {getUserIsAdmin() && props.idleMissionHandlerPods === 0 && (
                        <Card.Body className="px-4 py-0">
                            <Alert variant="danger">
                                No idle mission handlers available. When a task
                                is started, it may take some time before it
                                appears.
                            </Alert>
                        </Card.Body>
                    )}
                    {!!props.droneState?.missions &&
                        props.droneState?.missions.length > 0 && (
                            <>
                                <Card.Body className="px-4 py-0">
                                    <Alert variant="warning">
                                        Drone is in use by Mission Handler. Do
                                        not approach or touch the drone.
                                    </Alert>
                                </Card.Body>
                                {getUserIsAdmin() &&
                                    props.droneState?.missions.map(
                                        (mission) => {
                                            return (
                                                <MissionHandlerAlerts
                                                    organizationId={
                                                        mission.organizationId
                                                    }
                                                    locationId={
                                                        mission.locationId
                                                    }
                                                    missionId={
                                                        mission.missionId
                                                    }
                                                    key={mission.missionId}
                                                    heading={
                                                        <>
                                                            Mission{" "}
                                                            <TimeDisplay
                                                                timestamp={Math.floor(
                                                                    new Date(
                                                                        mission.timestamp,
                                                                    ).getTime() /
                                                                        1000,
                                                                )}
                                                                format="YYYY-MM-DD HH:mm:ss"
                                                                organizationId={
                                                                    mission.organizationId
                                                                }
                                                                locationId={
                                                                    mission.locationId
                                                                }
                                                            />
                                                        </>
                                                    }
                                                />
                                            );
                                        },
                                    )}
                            </>
                        )}
                    {!isDroneOnline && (
                        <Card.Body>
                            <Alert
                                className="text-center mb-0"
                                variant="secondary"
                            >
                                <div className="text-muted">
                                    Drone is offline
                                    {getUserIsAdmin() && (
                                        <>
                                            {" since "}
                                            <TimeDisplay
                                                timestamp={
                                                    props.droneState?.connection
                                                        ?.lastUpdate / 1000
                                                }
                                                format="dddd YYYY-MM-DD HH:mm:ss"
                                                hideDateIfToday={true}
                                                organizationId={
                                                    props.drone.organizationId
                                                }
                                                locationId={
                                                    props.drone.locationId
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                            </Alert>
                        </Card.Body>
                    )}
                    {props.droneState?.widgets &&
                        Object.keys(props.droneState.widgets).length > 0 && (
                            <>
                                {isDroneOnline && (
                                    <Card.Body className="p-4 pt-0 mt-0">
                                        <DroneStatus
                                            droneStatus={
                                                props.droneState.widgets.status
                                            }
                                        />
                                    </Card.Body>
                                )}
                                {getUserIsAdmin() && (
                                    <ListGroup variant="flush">
                                        {!!props.droneState?.widgets
                                            ?.position && (
                                            <ListGroup.Item className="px-4">
                                                <Row>
                                                    <Col xs={12} md={3}>
                                                        <b>Position</b>
                                                    </Col>
                                                    <Col xs={12} md={9}>
                                                        <h5 className="d-inline">
                                                            <Badge
                                                                bg={
                                                                    isDroneOnline &&
                                                                    !isDroneSleeping
                                                                        ? "danger"
                                                                        : "secondary"
                                                                }
                                                                className="me-2"
                                                            >
                                                                <span className="me-2">
                                                                    x
                                                                </span>
                                                                {props.droneState.widgets.position.data.x.toFixed(
                                                                    2,
                                                                )}
                                                            </Badge>
                                                        </h5>
                                                        <h5 className="d-inline">
                                                            <Badge
                                                                bg={
                                                                    isDroneOnline &&
                                                                    !isDroneSleeping
                                                                        ? "success"
                                                                        : "secondary"
                                                                }
                                                                className="me-2"
                                                            >
                                                                <span className="me-2">
                                                                    y
                                                                </span>
                                                                {props.droneState.widgets.position.data.y.toFixed(
                                                                    2,
                                                                )}
                                                            </Badge>
                                                        </h5>
                                                        <h5 className="d-inline">
                                                            <Badge
                                                                bg={
                                                                    isDroneOnline &&
                                                                    !isDroneSleeping
                                                                        ? "primary"
                                                                        : "secondary"
                                                                }
                                                                className="me-2"
                                                            >
                                                                <span className="me-2">
                                                                    z
                                                                </span>
                                                                {props.droneState.widgets.position.data.z.toFixed(
                                                                    2,
                                                                )}
                                                            </Badge>
                                                        </h5>
                                                    </Col>
                                                </Row>
                                            </ListGroup.Item>
                                        )}
                                        {!!props.droneState?.widgets
                                            ?.orientation && (
                                            <ListGroup.Item className="px-4">
                                                <Row>
                                                    <Col xs={12} md={3}>
                                                        <b>Rotation</b>
                                                    </Col>
                                                    <Col xs={12} md={9}>
                                                        {!props.droneState
                                                            .widgets.orientation
                                                            .data.roll && (
                                                            <>
                                                                {!!props
                                                                    .droneState
                                                                    .widgets
                                                                    .orientation
                                                                    .data.x && (
                                                                    <span className="me-3">
                                                                        <span className="me-2">
                                                                            x
                                                                        </span>
                                                                        {props.droneState.widgets.orientation.data.x.toFixed(
                                                                            3,
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {!!props
                                                                    .droneState
                                                                    .widgets
                                                                    .orientation
                                                                    .data.y && (
                                                                    <span className="me-3">
                                                                        <span className="me-2">
                                                                            y
                                                                        </span>
                                                                        {props.droneState.widgets.orientation.data.y.toFixed(
                                                                            3,
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {!!props
                                                                    .droneState
                                                                    .widgets
                                                                    .orientation
                                                                    .data.z && (
                                                                    <span className="me-3">
                                                                        <span className="me-2">
                                                                            z
                                                                        </span>
                                                                        {props.droneState.widgets.orientation.data.z.toFixed(
                                                                            3,
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {!!props
                                                                    .droneState
                                                                    .widgets
                                                                    .orientation
                                                                    .data.w && (
                                                                    <span className="me-3">
                                                                        <span className="me-2">
                                                                            w
                                                                        </span>
                                                                        {props.droneState.widgets.orientation.data.w.toFixed(
                                                                            3,
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                        {!!props.droneState
                                                            .widgets.orientation
                                                            .data.roll && (
                                                            <>
                                                                {!!props
                                                                    .droneState
                                                                    .widgets
                                                                    .orientation
                                                                    .data
                                                                    .roll && (
                                                                    <h5 className="d-inline">
                                                                        <Badge
                                                                            bg={
                                                                                isDroneOnline &&
                                                                                !isDroneSleeping
                                                                                    ? "danger"
                                                                                    : "secondary"
                                                                            }
                                                                            className="me-2"
                                                                        >
                                                                            <span className="me-2">
                                                                                r
                                                                            </span>
                                                                            {props.droneState.widgets.orientation.data.roll.toFixed(
                                                                                1,
                                                                            )}
                                                                        </Badge>
                                                                    </h5>
                                                                )}
                                                                {!!props
                                                                    .droneState
                                                                    .widgets
                                                                    .orientation
                                                                    .data
                                                                    .pitch && (
                                                                    <h5 className="d-inline">
                                                                        <Badge
                                                                            bg={
                                                                                isDroneOnline &&
                                                                                !isDroneSleeping
                                                                                    ? "success"
                                                                                    : "secondary"
                                                                            }
                                                                            className="me-2"
                                                                        >
                                                                            <span className="me-2">
                                                                                p
                                                                            </span>
                                                                            {props.droneState.widgets.orientation.data.pitch.toFixed(
                                                                                1,
                                                                            )}
                                                                        </Badge>
                                                                    </h5>
                                                                )}
                                                                {!!props
                                                                    .droneState
                                                                    .widgets
                                                                    .orientation
                                                                    .data
                                                                    .yaw && (
                                                                    <h5 className="d-inline">
                                                                        <Badge
                                                                            bg={
                                                                                isDroneOnline &&
                                                                                !isDroneSleeping
                                                                                    ? "primary"
                                                                                    : "secondary"
                                                                            }
                                                                            className="me-2"
                                                                        >
                                                                            <span className="me-2">
                                                                                y
                                                                            </span>
                                                                            {props.droneState.widgets.orientation.data.yaw.toFixed(
                                                                                1,
                                                                            )}
                                                                        </Badge>
                                                                    </h5>
                                                                )}
                                                            </>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </ListGroup.Item>
                                        )}
                                        {!!props.location &&
                                            [
                                                "SIMULATION",
                                                "EMULATION",
                                            ].includes(
                                                props.drone.augmentation,
                                            ) && (
                                                <ListGroup.Item className="px-4">
                                                    <Row>
                                                        <Col xs={12} md={3}>
                                                            <b>System</b>
                                                        </Col>
                                                        <Col xs={12} md={9}>
                                                            <span className="me-3">
                                                                {props.drone
                                                                    .augmentation ===
                                                                    "SIMULATION" && (
                                                                    <h5 className="d-inline">
                                                                        <Badge
                                                                            bg={
                                                                                isDroneOnline
                                                                                    ? "info"
                                                                                    : "secondary"
                                                                            }
                                                                            className="me-2"
                                                                        >
                                                                            <FaCube className="me-2" />
                                                                            Simulated
                                                                        </Badge>
                                                                    </h5>
                                                                )}
                                                                {props.drone
                                                                    .augmentation ===
                                                                    "EMULATION" && (
                                                                    <h5 className="d-inline">
                                                                        <Badge
                                                                            bg={
                                                                                isDroneOnline
                                                                                    ? "info"
                                                                                    : "secondary"
                                                                            }
                                                                            className="me-2"
                                                                        >
                                                                            <FaServer className="me-2" />
                                                                            Emulated
                                                                        </Badge>
                                                                    </h5>
                                                                )}
                                                            </span>
                                                        </Col>
                                                    </Row>
                                                </ListGroup.Item>
                                            )}
                                        {!!props.location && (
                                            <ListGroup.Item className="px-4">
                                                <Row>
                                                    <Col xs={12} md={3}>
                                                        <b>Environment</b>
                                                    </Col>
                                                    <Col xs={12} md={9}>
                                                        <span className="me-3">
                                                            {props.location
                                                                ?.markerType ===
                                                                "ARGUS" && (
                                                                <h5 className="d-inline">
                                                                    <Badge
                                                                        bg={
                                                                            isDroneOnline
                                                                                ? "success"
                                                                                : "secondary"
                                                                        }
                                                                        className="me-2"
                                                                    >
                                                                        <FaQrcode className="me-2" />
                                                                        Argus
                                                                    </Badge>
                                                                </h5>
                                                            )}
                                                            {props.location
                                                                ?.markerType !==
                                                                "ARGUS" && (
                                                                <h5 className="d-inline">
                                                                    <Badge
                                                                        bg={
                                                                            isDroneOnline
                                                                                ? "danger"
                                                                                : "secondary"
                                                                        }
                                                                        className="me-2"
                                                                    >
                                                                        <FaQrcode className="me-2" />
                                                                        Aruco
                                                                    </Badge>
                                                                </h5>
                                                            )}
                                                            {props.drone
                                                                .routeplanner
                                                                .version && (
                                                                <h5 className="d-inline">
                                                                    <Badge
                                                                        bg={
                                                                            isDroneOnline
                                                                                ? "success"
                                                                                : "secondary"
                                                                        }
                                                                        className="me-2"
                                                                    >
                                                                        <FaRoute className="me-2" />

                                                                        {
                                                                            props
                                                                                .drone
                                                                                .routeplanner
                                                                                .version
                                                                        }
                                                                        {routeplannerDoc?.version &&
                                                                            ` (${routeplannerDoc.version})`}
                                                                    </Badge>
                                                                </h5>
                                                            )}
                                                            {props.drone.docks
                                                                .length > 0 ? (
                                                                props.drone.docks.map(
                                                                    (dock) => {
                                                                        const dockInfo =
                                                                            docks.find(
                                                                                (
                                                                                    d,
                                                                                ) =>
                                                                                    d.id ===
                                                                                    dock.id,
                                                                            );
                                                                        // Don't show any docks created by the mission handler
                                                                        return (
                                                                            (dockInfo?.name ===
                                                                                undefined ||
                                                                                !dockInfo?.name.startsWith(
                                                                                    "mobile_",
                                                                                )) && (
                                                                                <h5
                                                                                    className="d-inline"
                                                                                    key={
                                                                                        dock.id
                                                                                    }
                                                                                >
                                                                                    <Badge
                                                                                        bg={
                                                                                            isDroneOnline
                                                                                                ? "success"
                                                                                                : "secondary"
                                                                                        }
                                                                                        className="me-2"
                                                                                        onClick={
                                                                                            dock.id !==
                                                                                            "mobile"
                                                                                                ? () => {
                                                                                                      navigate(
                                                                                                          urls.dock(
                                                                                                              props
                                                                                                                  .drone
                                                                                                                  .organizationId,
                                                                                                              props
                                                                                                                  .drone
                                                                                                                  .locationId,
                                                                                                              dock.id,
                                                                                                          ),
                                                                                                      );
                                                                                                  }
                                                                                                : undefined
                                                                                        }
                                                                                        style={
                                                                                            dock.id !==
                                                                                            "mobile"
                                                                                                ? {
                                                                                                      cursor: "pointer",
                                                                                                      border: "none",
                                                                                                      background:
                                                                                                          "none",
                                                                                                  }
                                                                                                : {}
                                                                                        }
                                                                                    >
                                                                                        <FaHome className="me-2" />
                                                                                        {dock.id ===
                                                                                            "mobile" &&
                                                                                            "Mobile Dock"}
                                                                                        {dock.id !==
                                                                                            "mobile" &&
                                                                                            (dockInfo?.name ||
                                                                                                "Dock")}
                                                                                    </Badge>
                                                                                </h5>
                                                                            )
                                                                        );
                                                                    },
                                                                )
                                                            ) : (
                                                                <h5 className="d-inline">
                                                                    <Badge
                                                                        bg={
                                                                            isDroneOnline
                                                                                ? "danger"
                                                                                : "secondary"
                                                                        }
                                                                    >
                                                                        <FaExclamationTriangle className="me-2" />
                                                                        No Dock
                                                                    </Badge>
                                                                </h5>
                                                            )}
                                                        </span>
                                                    </Col>
                                                </Row>
                                            </ListGroup.Item>
                                        )}
                                        {
                                            <ListGroup.Item className="px-4">
                                                <Row>
                                                    <Col xs={12} md={3}>
                                                        <b>Upload status</b>
                                                    </Col>
                                                    <Col xs={12} md={9}>
                                                        <span className="me-3">
                                                            <h5 className="d-inline">
                                                                <Badge
                                                                    bg={
                                                                        isDroneOnline
                                                                            ? logUploaderStatus?.color ||
                                                                              "info"
                                                                            : "secondary"
                                                                    }
                                                                    className="me-2"
                                                                >
                                                                    {(logUploaderStatus?.state ===
                                                                        "blocked") &
                                                                    (logUploaderStatus?.state !==
                                                                        "disabled") ? (
                                                                        <FaPause
                                                                            color="light"
                                                                            className="me-1"
                                                                        />
                                                                    ) : (
                                                                        ""
                                                                    )}
                                                                    {logUploaderStatus?.state ===
                                                                        "disabled" && (
                                                                        <MdBlock
                                                                            color="light"
                                                                            className="me-1"
                                                                        />
                                                                    )}
                                                                    <FaFileWaveform color="light" />
                                                                    {` ${logUploaderStatus?.files}`}
                                                                    <FaFolder
                                                                        color="light"
                                                                        className="ms-2"
                                                                    />
                                                                    {` ${logUploaderStatus?.folders}`}
                                                                    {` ${logUploaderStatus?.size}`}
                                                                </Badge>

                                                                <Badge
                                                                    bg={
                                                                        isDroneOnline
                                                                            ? payloadUploaderStatus?.color
                                                                            : "secondary"
                                                                    }
                                                                >
                                                                    {(payloadUploaderStatus?.state ===
                                                                        "blocked") &
                                                                    (payloadUploaderStatus?.state !==
                                                                        "disabled") ? (
                                                                        <FaPause
                                                                            color="light"
                                                                            className="me-1"
                                                                        />
                                                                    ) : (
                                                                        ""
                                                                    )}
                                                                    {payloadUploaderStatus?.state ===
                                                                        "disabled" && (
                                                                        <MdBlock
                                                                            color="light"
                                                                            className="me-1"
                                                                        />
                                                                    )}
                                                                    <FaImage color="light" />
                                                                    {` ${payloadUploaderStatus?.files}`}
                                                                    <FaFolder
                                                                        color="light"
                                                                        className="ms-2"
                                                                    />
                                                                    {` ${payloadUploaderStatus?.folders}`}
                                                                    {` ${payloadUploaderStatus?.size}`}
                                                                </Badge>
                                                            </h5>
                                                        </span>
                                                    </Col>
                                                </Row>
                                            </ListGroup.Item>
                                        }
                                        <ListGroup.Item className="px-4">
                                            <Row>
                                                <Col xs={12} md={3}>
                                                    <b>Hardware</b>
                                                </Col>
                                                <Col xs={12} md={9}>
                                                    <span className="me-3">
                                                        <h5 className="d-inline">
                                                            <Badge
                                                                bg={
                                                                    isDroneOnline
                                                                        ? "success"
                                                                        : "secondary"
                                                                }
                                                                className="me-2"
                                                            >
                                                                <img
                                                                    src="/E13 icon.svg"
                                                                    width="auto"
                                                                    height="14"
                                                                    className="me-2"
                                                                    style={{
                                                                        marginTop:
                                                                            "-2px",
                                                                    }}
                                                                    alt="Drone model"
                                                                />
                                                                {
                                                                    props.drone
                                                                        ?.model
                                                                }
                                                            </Badge>
                                                            {props.droneState
                                                                .widgets
                                                                ?.versions?.data
                                                                ?.battery && (
                                                                <Badge
                                                                    bg={
                                                                        isDroneOnline
                                                                            ? "success"
                                                                            : "secondary"
                                                                    }
                                                                    className="me-2"
                                                                >
                                                                    <FaBatteryFull className="me-1" />
                                                                    {
                                                                        props
                                                                            .droneState
                                                                            .widgets
                                                                            ?.versions
                                                                            ?.data
                                                                            ?.battery
                                                                    }
                                                                </Badge>
                                                            )}
                                                        </h5>
                                                    </span>
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                        {versionComponents.some(
                                            (c) => c.version,
                                        ) && (
                                            <ListGroup.Item className="px-4">
                                                <Row>
                                                    <Col xs={12} md={3}>
                                                        <b>Versions</b>
                                                    </Col>
                                                    <Col xs={12} md={9}>
                                                        <span className="me-3">
                                                            <h5 className="d-inline">
                                                                {versionComponents.map(
                                                                    ({
                                                                        key,
                                                                        label,
                                                                        version,
                                                                        link,
                                                                        showVersion,
                                                                    }) => {
                                                                        if (
                                                                            !version
                                                                        )
                                                                            return null;

                                                                        const variant =
                                                                            getVersionVariant(
                                                                                {
                                                                                    isOnline:
                                                                                        isDroneOnline,
                                                                                    version,
                                                                                    policy: versions?.[
                                                                                        key
                                                                                    ],
                                                                                },
                                                                            );

                                                                        const hoverText =
                                                                            versions?.[
                                                                                key
                                                                            ]?.upToDate?.includes(
                                                                                version,
                                                                            )
                                                                                ? "Up to date"
                                                                                : versions?.[
                                                                                        key
                                                                                    ]?.updateRecommended?.includes(
                                                                                        version,
                                                                                    )
                                                                                  ? "Update recommended"
                                                                                  : version.includes(
                                                                                          "dev",
                                                                                      )
                                                                                    ? "Development version"
                                                                                    : "Outdated";

                                                                        const badge =
                                                                            (
                                                                                <OverlayTrigger
                                                                                    key={
                                                                                        key
                                                                                    }
                                                                                    delay={{
                                                                                        show: 250,
                                                                                    }}
                                                                                    overlay={
                                                                                        <Tooltip
                                                                                            id={`overlay-${key}`}
                                                                                        >
                                                                                            {
                                                                                                hoverText
                                                                                            }
                                                                                        </Tooltip>
                                                                                    }
                                                                                >
                                                                                    <Badge
                                                                                        className={`me-2 bg-${variant}`}
                                                                                    >
                                                                                        {showVersion !==
                                                                                        false
                                                                                            ? `${label}: ${version}`
                                                                                            : label}
                                                                                    </Badge>
                                                                                </OverlayTrigger>
                                                                            );

                                                                        return link ? (
                                                                            <a
                                                                                key={
                                                                                    key
                                                                                }
                                                                                href={
                                                                                    link
                                                                                }
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                            >
                                                                                {
                                                                                    badge
                                                                                }
                                                                            </a>
                                                                        ) : (
                                                                            badge
                                                                        );
                                                                    },
                                                                )}
                                                            </h5>
                                                        </span>
                                                    </Col>
                                                </Row>
                                            </ListGroup.Item>
                                        )}
                                        {!!props.droneState?.missions &&
                                            props.droneState?.missions.length >
                                                0 && (
                                                <ListGroup.Item className="px-4">
                                                    <Row>
                                                        <Col xs={12} md={3}>
                                                            <b>
                                                                Active missions
                                                            </b>
                                                        </Col>
                                                        <Col xs={12} md={9}>
                                                            {props.droneState?.missions.map(
                                                                (mission) => (
                                                                    <h5
                                                                        className="d-inline"
                                                                        key={`badge-${mission.missionId}`}
                                                                    >
                                                                        <Badge
                                                                            bg={`${isDroneOnline ? "success" : "secondary"} me-2`}
                                                                            onClick={() => {
                                                                                navigate(
                                                                                    urls.locationMission(
                                                                                        mission.organizationId,
                                                                                        mission.locationId,
                                                                                        mission.missionId,
                                                                                    ),
                                                                                );
                                                                            }}
                                                                            style={{
                                                                                cursor: "pointer",
                                                                                border: "none",
                                                                                background:
                                                                                    "none",
                                                                            }}
                                                                        >
                                                                            <TimeDisplay
                                                                                timestamp={Math.floor(
                                                                                    new Date(
                                                                                        mission.timestamp,
                                                                                    ).getTime() /
                                                                                        1000,
                                                                                )}
                                                                                format="YYYY-MM-DD HH:mm:ss"
                                                                                organizationId={
                                                                                    mission.organizationId
                                                                                }
                                                                                locationId={
                                                                                    mission.locationId
                                                                                }
                                                                            />
                                                                        </Badge>
                                                                    </h5>
                                                                ),
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </ListGroup.Item>
                                            )}
                                    </ListGroup>
                                )}
                            </>
                        )}
                </Card>
                {activeMission && (
                    <UserQuestion
                        organizationId={props.drone.organizationId}
                        locationId={props.drone.locationId}
                        mission={activeMission}
                    ></UserQuestion>
                )}
                {thisDroneIsFlying() && (
                    <>
                        {(showReturnToHomeButton() ||
                            showPauseButton() ||
                            showMapButton()) && (
                            <Row>
                                <Col className="w-100 d-flex align-items-center justify-content-center">
                                    <span>
                                        {showReturnToHomeButton() && (
                                            <Button
                                                variant="primary"
                                                className="mt-2 me-2"
                                                disabled={
                                                    props.droneState?.widgets
                                                        ?.bestReturnCommand
                                                        ?.data === ""
                                                }
                                                onClick={() =>
                                                    setShowReturnToHomeModal(
                                                        true,
                                                    )
                                                }
                                            >
                                                <FaHome size="30px" />
                                            </Button>
                                        )}
                                        {showPauseButton() &&
                                            (api.droneIsPaused(
                                                props.droneState.widgets,
                                            ) ? (
                                                <Button
                                                    variant="primary"
                                                    className="mt-2 me-2"
                                                    onClick={() =>
                                                        api.sendResume(
                                                            props.drone.id,
                                                        )
                                                    }
                                                >
                                                    <FaPlay size="30px" />
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="primary"
                                                    className="mt-2 me-2"
                                                    onClick={() =>
                                                        api.sendPause(
                                                            props.drone.id,
                                                        )
                                                    }
                                                >
                                                    <FaPause size="30px" />
                                                </Button>
                                            ))}
                                        {showMapButton() && (
                                            <Button
                                                variant="outline-secondary"
                                                className="mt-2"
                                                as={Link}
                                                to={`${urls.locationMap(props.drone.organizationId, props.drone.locationId)}?flightId=${props.droneState.widgets.currentFlightId.data}`}
                                            >
                                                <FaMap size="30px" />
                                            </Button>
                                        )}
                                    </span>
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col className="w-100 d-flex align-items-center justify-content-center">
                                <div className="w-50">
                                    <EmergencyLandSlider
                                        drone={props.drone}
                                        droneState={props.droneState}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
                {(isDroneOnline || getUserIsAdmin()) && (
                    <Row>
                        <Col xs={12} md={6} className="mt-4">
                            <DroneBatteryStat
                                offline={!isDroneOnline}
                                batteryInfo={
                                    props.droneState?.widgets?.batteryInfo?.data
                                }
                                batteryPercentageMode={
                                    props.drone?.powermodule
                                        ?.capacity_based_battery
                                }
                                batteryVersion={
                                    props.droneState?.widgets?.versions?.data
                                        ?.battery
                                }
                                remainingFlightDuration={
                                    props.droneState?.widgets
                                        ?.remainingFlightDuration?.data
                                }
                                oldBatteryPercentage={
                                    props.droneState?.widgets?.battery?.data
                                }
                                forAdmin={getUserIsAdmin()}
                            />
                        </Col>
                        <Col xs={12} md={6} className="mt-4">
                            <DroneAccuracyStat
                                accuracy={
                                    props.droneState?.widgets?.positionAccuracy?.data.toFixed(
                                        3,
                                    ) || -1
                                }
                                droneStatus={
                                    props.droneState?.widgets?.status?.data
                                }
                                offline={!isDroneOnline}
                                sleeping={isDroneSleeping}
                                forAdmin={getUserIsAdmin()}
                            />
                        </Col>
                        <Col xs={12} md={6} className="mt-4">
                            <DroneWifiStat
                                network={props.droneState?.widgets?.voxl?.data}
                                forAdmin={getUserIsAdmin()}
                                onClick={() => setShowWifiModal(true)}
                                offline={!isDroneOnline}
                            />
                        </Col>
                        {showDiskSpaceState && (
                            <Col xs={12} md={6} className="mt-4">
                                <DroneDiskSpaceStat
                                    availableMB={
                                        props.droneState?.widgets
                                            ?.diskAvailableMB?.data
                                    }
                                    totalMB={
                                        props.droneState?.widgets?.diskTotalMB
                                            ?.data
                                    }
                                    minAvailableMB={
                                        props.drone?.navcontrol
                                            ?.preflight_checks
                                            ?.min_disk_available_mb
                                    }
                                    offline={!isDroneOnline}
                                />
                            </Col>
                        )}
                        <Col
                            xs={12}
                            md={showDiskSpaceState ? 12 : 6}
                            className="mt-4"
                        >
                            <DroneStateStat
                                status={
                                    props.droneState?.widgets?.status?.data ||
                                    "n/a"
                                }
                                offline={!isDroneOnline}
                            />
                        </Col>
                    </Row>
                )}
                <DroneMissionList
                    className="mt-4"
                    limit={3}
                    droneId={props.drone.id}
                />
            </Layout>
        </>
    );
}

export default function DronePage() {
    const { droneId, organizationId, locationId } = useParams();
    const [drone] = useDroneConfigDocument(droneId);
    const [location] = useLocationDocument(organizationId, locationId);
    const [droneState] = useDroneStateRtdbDocument(droneId);

    const navigate = useNavigate();

    const [missionHandlers] = useMissionHandlerClusterDocument(
        location?.missionHandler?.version,
    );
    const idleMissionHandlerPods = missionHandlers?.idle?.length;

    useEffect(() => {
        if (droneId && droneId !== droneId.toUpperCase()) {
            navigate(urls.drone(droneId.toUpperCase()), { replace: true });
        } else if (
            drone &&
            (drone.organizationId !== organizationId ||
                drone.locationId !== locationId)
        ) {
            navigate(
                urls.locationDrone(
                    drone.organizationId,
                    drone.locationId,
                    droneId.toUpperCase(),
                ),
                { replace: true },
            );
        }
    }, [drone, organizationId, locationId, droneId, navigate]);

    if (!drone) {
        // TODO : embed in loading screen or upgrade to React 18
        return null;
    }

    return (
        <Drone
            organizationId={organizationId}
            locationId={locationId}
            location={location}
            drone={drone}
            droneState={droneState}
            idleMissionHandlerPods={idleMissionHandlerPods}
        />
    );
}
